import { useState, useRef, useEffect, useCallback } from 'react';

interface UseDialProps {
  onChange?: (val: number) => void;
  initValue?: number;
  initClipDegree?: number;
  segments?: number; // Optional: number of segments, defaults to undefined
}

export const useDialMove = ({
  onChange,
  initValue = 0.5,
  initClipDegree = 45,
  segments, // Defaults to undefined, no segmentation validation if not provided
}: UseDialProps) => {
  const [value, setValue] = useState(initValue);
  const [isDragging, setIsDragging] = useState(false);
  const fullDegree = 360 - initClipDegree * 2; // Effective angle range
  const segmentDegree = segments ? fullDegree / segments : undefined; // Angle per segment (if segments are provided)
  const dialRef = useRef<HTMLDivElement>(null);
  const lastDegreeRef = useRef<number>(0);

  const calculateValueFromEvent = useCallback(
    (e: MouseEvent) => {
      if (dialRef.current) {
        const rect = dialRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2; // X-coordinate of the center
        const centerY = rect.top + rect.height / 2; // Y-coordinate of the center
        const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX);

        let degree = (angle * (180 / Math.PI) + 270) % 360;

        // Restrict the degree within the valid range
        if (degree < initClipDegree) degree = initClipDegree;
        if (degree > 360 - initClipDegree) degree = 360 - initClipDegree;

        const lastDegree = lastDegreeRef.current;

        // Prevent jumping across 0 degrees
        if (Math.abs(degree - lastDegree) > 180) {
          return;
        }

        lastDegreeRef.current = degree;

        // Determine whether to apply segmentation validation
        if (segments && segmentDegree) {
          // Find the nearest segment value
          const relativeDegree = degree - initClipDegree; // Angle within the effective range
          const segmentIndex = Math.round(relativeDegree / segmentDegree); // Nearest segment index
          const newValue = parseFloat((segmentIndex / segments).toFixed(2)); // Calculate the new value

          if (newValue !== value) {
            setValue(newValue);
            onChange?.(newValue);
          }
        } else {
          // Without segmentation, calculate the value based on proportion
          const newValue = parseFloat(((degree - initClipDegree) / fullDegree).toFixed(2));
          if (newValue !== value) {
            setValue(newValue);
            onChange?.(newValue);
          }
        }
      }
    },
    [onChange, initClipDegree, fullDegree, segmentDegree, segments, value],
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) calculateValueFromEvent(e);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, calculateValueFromEvent]);

  const startDragging = () => setIsDragging(true);

  return {
    value,
    startDragging,
    dialRef,
  };
};
